// src/components/AppleSignInButton.tsx
// Sign in with Apple ボタン（App Store Guideline 4.8 対応）
// Apple の HIG に準拠した黒背景・白ロゴのボタン
import React from 'react';
import { Button, SxProps, Theme } from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import { signInWithApple, isNativePlatform } from '@/services/authService';

interface Props {
    label?: string;
    fullWidth?: boolean;
    size?: 'small' | 'medium' | 'large';
    sx?: SxProps<Theme>;
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
}

const AppleSignInButton: React.FC<Props> = ({
    label = 'Appleでサインイン',
    fullWidth = false,
    size = 'medium',
    sx,
    onSuccess,
    onError,
}) => {
    // Web では Apple サインイン非対応(未構成)のため、iOS ネイティブのみ表示する
    if (!isNativePlatform()) {
        return null;
    }

    const handleClick = async () => {
        try {
            await signInWithApple();
            // サインイン後は AuthProvider の onIdTokenChanged が発火
            onSuccess?.();
        } catch (e: any) {
            // ユーザーによるキャンセルはエラー扱いしない
            const msg = String(e?.message ?? e ?? '');
            if (msg.includes('canceled') || msg.includes('cancelled') || e?.code === 'auth/popup-closed-by-user' || e?.code === '1001') {
                return;
            }
            console.error('Appleログインでエラーが発生しました:', e);
            // Web版でAppleプロバイダが未構成の場合は、生のエラーコードを見せず案内する
            const friendly =
                e?.code === 'auth/operation-not-allowed'
                    ? 'WebでのAppleサインインは現在準備中です。お手数ですがGoogleログインをご利用ください（iOSアプリではAppleサインインをご利用いただけます）。'
                    : `Appleサインインに失敗しました。${msg ? `\n${msg}` : 'しばらくしてから再度お試しください。'}`;
            // 呼び出し側が onError を渡していない場合でも、失敗が無反応に見えないよう通知する
            if (onError) {
                onError(e);
            } else if (typeof window !== 'undefined') {
                window.alert(friendly);
            }
        }
    };

    return (
        <Button
            variant="contained"
            size={size}
            fullWidth={fullWidth}
            startIcon={<AppleIcon />}
            onClick={handleClick}
            sx={{
                backgroundColor: '#000',
                color: '#fff',
                whiteSpace: 'nowrap',
                '&:hover': { backgroundColor: '#222' },
                ...sx,
            }}
        >
            {label}
        </Button>
    );
};

export default AppleSignInButton;
